import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
  type Ref,
} from "react";

import { focusRing } from "../../foundations/a11y";
import { cn } from "../../lib/cn";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "neutral"
  | "danger"
  | "ghost"
  | "link";
export type ButtonSize = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 rounded-md font-sans font-medium transition-[transform,background-color,color] duration-fast ease-standard active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none motion-reduce:transition-none motion-reduce:active:scale-100";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-accent text-on-accent hover:bg-accent-hover",
  secondary: "bg-fill-secondary text-text hover:bg-border",
  neutral:
    "border border-separator bg-surface text-text-secondary hover:border-border hover:bg-fill hover:text-text",
  danger: "bg-error text-on-accent hover:bg-error-text",
  ghost: "bg-transparent text-text hover:bg-fill",
  link: "bg-transparent text-accent underline-offset-4 hover:underline px-0",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-label",
  md: "h-10 px-4 text-body",
  lg: "h-12 px-6 text-body-lg",
};

type ButtonOwnProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  as?: ElementType;
  children?: ReactNode;
};

export type ButtonProps = ButtonOwnProps &
  Omit<ComponentPropsWithoutRef<"button">, keyof ButtonOwnProps>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "primary",
      size = "md",
      loading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      as,
      className,
      children,
      disabled,
      ...rest
    },
    ref,
  ) {
    const Component = (as ?? "button") as ElementType;
    const isNativeButton = Component === "button";
    return (
      <Component
        ref={ref as Ref<never>}
        className={cn(
          base,
          sizes[size],
          variants[variant],
          fullWidth && "w-full",
          focusRing,
          className,
        )}
        aria-busy={loading || undefined}
        disabled={isNativeButton ? disabled || loading : undefined}
        aria-disabled={
          !isNativeButton && (disabled || loading) ? true : undefined
        }
        {...rest}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </Component>
    );
  },
);
